import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckIcon from "@mui/icons-material/Check";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

import PageLoader from "components/common/PageLoader";
import TalentAvatar from "components/Screens/Marketplace/Customer/Shortlist/TalentAvatar";
import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import { getTalentDisplayName } from "components/Screens/Marketplace/Customer/utils/redaction";
import { andList } from "components/Screens/Marketplace/Customer/utils/preferenceTiles";
import { fmtDay, timeZoneForState } from "components/Screens/Marketplace/Customer/utils/availability";
import { buildIcs, downloadIcs } from "components/Screens/Marketplace/Customer/utils/ics";
import type { InterviewRound } from "components/Screens/Marketplace/Customer/types";
import "components/Screens/Marketplace/Customer/Shortlist/Shortlist.scss";
import "./InterviewStatus.scss";

const PILL: Record<InterviewRound["status"], [string, string]> = {
  pending: ["Scheduling pending", "st-pill--pending"],
  scheduled: ["Scheduled", "st-pill--scheduled"],
  completed: ["Completed", "st-pill--completed"],
};

const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

/** "Asad U., Maria L. and one seat Edge is filling" */
export function roundNames(round: InterviewRound): string {
  const known = round.talents.map(({ talent }) => getTalentDisplayName(talent));
  const open = round.openSeats || 0;
  return andList(open ? [...known, open === 1 ? "one seat Edge is filling" : `${open === 2 ? "two" : open} seats Edge is filling`] : known);
}

function RoundCard({ round, tz }: { round: InterviewRound; tz: string }) {
  const { routes } = useTalentApi();
  const names = roundNames(round);
  const openSeats = round.openSeats || 0;
  const [pill, pillCls] = PILL[round.status] || PILL.pending;
  const { schedule } = round;

  const addToCalendar = () => {
    if (!schedule) return;
    downloadIcs(
      `edge-interview-round-${round.roundNumber}.ics`,
      buildIcs({
        uid: `${round.id}@onedge.co`,
        title: `Edge interviews: ${names}`,
        startAt: schedule.startAt,
        endAt: schedule.endAt,
        url: schedule.meetingUrl,
        description: `Interview round ${round.roundNumber} with ${names}`,
      })
    );
  };

  return (
    <div className="st-card plg-card">
      <div className="st-top">
        <div className="st-who">
          <div className="st-tag">{`Interview round ${round.roundNumber}`}</div>
          <div className="st-names">{names}</div>
        </div>
        <span className={`st-pill ${pillCls}`}>{pill}</span>
      </div>
      <div className="st-group">
        {round.talents.map(({ talent }) => (
          // eslint-disable-next-line no-underscore-dangle
          <Link className="st-person" key={talent._id} to={routes.talentProfile(talent._id)}>
            <TalentAvatar talent={talent} size={40} />
            <span className="st-pname">{getTalentDisplayName(talent)}</span>
          </Link>
        ))}
        {Array.from({ length: openSeats }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="st-person st-seat" key={`open-${i}`}>
            <span className="st-seat__ph"><AutoAwesomeOutlinedIcon fontSize="small" /></span>
            <span>
              <span className="st-pname">Edge is finding someone</span>
              <span className="st-prole">Based on what you told us</span>
            </span>
          </div>
        ))}
      </div>
      <div className="st-body">
        {round.status === "pending" && openSeats > 0 && (
          <>{`Edge is finding someone for the open ${openSeats === 1 ? "seat" : "seats"}. We will coordinate times once all three are confirmed.`}</>
        )}
        {round.status === "pending" && !openSeats && (
          <>
            Requested on <span className="st-when">{fmtDay(round.createdAt)}</span>
            <br />
            We are coordinating times with the candidates.
          </>
        )}
        {round.status === "scheduled" && (
          <>
            {schedule ? (
              <>
                <span className="st-when">{`${fmtDay(schedule.startAt)}, ${fmtTime(schedule.startAt)} to ${fmtTime(schedule.endAt)} ${tz}`}</span>
                <div className="st-actions">
                  {schedule.meetingUrl && (
                    <a className="st-btn st-btn--primary" href={schedule.meetingUrl} target="_blank" rel="noopener noreferrer">
                      <VideocamOutlinedIcon fontSize="small" />
                      Join video call
                    </a>
                  )}
                  <button type="button" className="st-btn" onClick={addToCalendar}>
                    <CalendarTodayOutlinedIcon fontSize="small" />
                    Add to calendar
                  </button>
                </div>
              </>
            ) : (
              "Edge is confirming the time with you."
            )}
            <div className="st-note">
              <CheckIcon fontSize="small" />
              The intro video and full name are now unlocked for these candidates.
            </div>
          </>
        )}
        {round.status === "completed" && (
          <>
            Interviewed on <span className="st-when">{fmtDay(schedule?.startAt || round.createdAt)}</span>
            <br />
            Your account executive will be in touch.
          </>
        )}
      </div>
    </div>
  );
}

function InterviewStatusPage() {
  const api = useTalentApi();
  const state = api.customerState;
  const [rounds, setRounds] = useState<InterviewRound[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.fetchInterviewRounds()
      .then((r) => setRounds([...r].sort((a, b) => b.roundNumber - a.roundNumber)))
      .catch(() => setError(true));
  }, []);

  const tz = timeZoneForState(state);

  return (
    <div className="sl-page">
      <Helmet>
        <title>Edge | Interview status</title>
      </Helmet>
      {!rounds && !error && <PageLoader />}
      <div className="sl-page__col">
        <div className="sl-page__head">
          <div>
            <h1 className="heading-h2-semibold">Interview status</h1>
            <p className="para-body-m-regular color-text-body-secondary">
              Each interview is one call with three people. Edge updates it as it moves along.
            </p>
          </div>
          <Link to={api.routes.shortlist} className="sl-link">Back to shortlist</Link>
        </div>
        {error && <div className="sl-empty"><h3>We could not load your interviews</h3><p>Please reload the page.</p></div>}
        {rounds && rounds.length === 0 && (
          <div className="sl-empty">
            <h3>No interview requests yet</h3>
            <p>Shortlist up to 3 people and request interviews from your shortlist.</p>
          </div>
        )}
        {rounds?.map((round) => <RoundCard key={round.id} round={round} tz={tz} />)}
      </div>
    </div>
  );
}

export default InterviewStatusPage;
